import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Trash2, Mail, Package, ShoppingCart, Activity, DollarSign, LogOut, Bot, Zap, Send, User, TrendingUp, CalendarClock, CreditCard, AlertCircle } from "lucide-react";
import api, { productImage } from "@/lib/api";

const HTTP_UNAUTHORIZED = 401;
const ADMIN_REFRESH_INTERVAL_MS = 8000;

// Curated collections (display-only mapping over the existing `category` field — Priority C product ladder).
const COLLECTION_MAP = {
  "Prompt Packs": "AI & Content Creation",
  "AI Skills Guides": "AI & Content Creation",
  "LoRA Style Packs": "AI & Content Creation",
  "ComfyUI Workflows": "AI & Content Creation",
  "Templates": "Templates & Toolkits",
  "Automation Templates": "Templates & Toolkits",
  "Presets": "Templates & Toolkits",
  "Wallpapers": "Templates & Toolkits",
  "Life Guides": "Guides & Playbooks",
  "Health Guides": "Guides & Playbooks",
  "Audiobooks": "Guides & Playbooks",
  "Digital": "Digital Marketing",
  "Bundles": "Business Growth",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activity, setActivity] = useState([]);
  const [stats, setStats] = useState({ total_products: 0, ai_products: 0, total_orders: 0, revenue: 0, profit: 0, aov: 0, pending_orders: 0, total_customers: 0, revenue_7d: [], top_products: [], subscribers: 0, abandoned_carts: 0, recovered_revenue: 0, draft_products: 0, insights: [] });
  const [auth, setAuth] = useState(true);

  // Generate dialog
  const [genOpen, setGenOpen] = useState(false);
  const [genTheme, setGenTheme] = useState("");
  const [genCategory, setGenCategory] = useState("Print");
  const [genBusy, setGenBusy] = useState(false);

  // Email dialog
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailKind, setEmailKind] = useState("welcome");
  const [emailAudience, setEmailAudience] = useState("new subscribers");
  const [emailDraft, setEmailDraft] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailSendTo, setEmailSendTo] = useState("");
  const [emailSendBusy, setEmailSendBusy] = useState(false);
  const [emailSendMsg, setEmailSendMsg] = useState("");
  const [emailConfigured, setEmailConfigured] = useState(false);

  // Auto-drop dialog
  const [dropOpen, setDropOpen] = useState(false);
  const [dropCount, setDropCount] = useState(3);
  const [dropCategory, setDropCategory] = useState("Print");
  const [dropThemes, setDropThemes] = useState("");

  // Niche-kit dialog
  const [nicheOpen, setNicheOpen] = useState(false);
  const [nicheBusy, setNicheBusy] = useState(false);
  const [nicheForm, setNicheForm] = useState({
    niche: "",
    kit_type: "Prompt Kit",
    price: 29,
    section_a_label: "Property Listings",
    section_a_count: 20,
    section_b_label: "Email Marketing",
    section_b_count: 20,
    section_c_label: "Social Media Strategy",
    section_c_count: 10,
    featured: true,
  });

  // Abandoned-cart recovery state
  const [recoveryPending, setRecoveryPending] = useState([]);
  const [recoveryRecovered, setRecoveryRecovered] = useState(0);
  const [recoveryStage2Pending, setRecoveryStage2Pending] = useState(0);
  const [recoveryBusy, setRecoveryBusy] = useState(false);

  // Bundle-builder dialog
  const [bundleOpen, setBundleOpen] = useState(false);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [bundleCandidates, setBundleCandidates] = useState([]);
  const [bundleForm, setBundleForm] = useState({ title: "", price: 44, product_ids: [] });
  const [bundleMsg, setBundleMsg] = useState("");
  const [recoveryMsg, setRecoveryMsg] = useState("");
  const [dropBusy, setDropBusy] = useState(false);
  const [dropResult, setDropResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, o, a, s] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/orders"),
        api.get("/admin/activity"),
        api.get("/admin/stats"),
      ]);
      setProducts(p.data); setOrders(o.data); setActivity(a.data); setStats(s.data);
    } catch (e) {
      if (e?.response?.status === HTTP_UNAUTHORIZED) { setAuth(false); nav("/admin/login"); }
    }
  }, [nav]);

  useEffect(() => {
    // Cookie-based auth: try to load; if it 401s the loader redirects to /admin/login.
    load();
    const t = setInterval(load, ADMIN_REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load, nav]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.error("logout failed", err); }
    nav("/admin/login");
  };

  const generate = async () => {
    if (!genTheme.trim()) return;
    setGenBusy(true);
    try {
      const r = await api.post("/admin/products/ai-generate", { theme: genTheme, category: genCategory });
      setGenOpen(false); setGenTheme("");
      alert(r?.data?.message || "Product generation started — it will appear in the catalog in ~30-120s. Watch the activity feed.");
      // Refresh once now (so the activity feed shows the 'started' entry) and again after ~90s to catch the finished product.
      await load();
      setTimeout(() => { load(); }, 90000);
    } catch (e) {
      alert("Generation failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setGenBusy(false);
    }
  };

  const runNicheKit = async () => {
    if (!nicheForm.niche.trim()) return;
    setNicheBusy(true);
    try {
      const r = await api.post("/admin/products/niche-kit", {
        ...nicheForm,
        price: Number(nicheForm.price) || 29,
        section_a_count: Number(nicheForm.section_a_count) || 20,
        section_b_count: Number(nicheForm.section_b_count) || 20,
        section_c_count: Number(nicheForm.section_c_count) || 10,
      });
      setNicheOpen(false);
      alert(r?.data?.message || "Niche kit generation started — the product will appear in the catalog in ~60-120s. Watch the activity feed.");
      await load();
      setTimeout(() => { load(); }, 120000);
    } catch (e) {
      alert("Niche kit failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setNicheBusy(false);
    }
  };

  const draftEmail = async () => {
    setEmailBusy(true); setEmailDraft("");
    try {
      const r = await api.post("/admin/ai/draft-email", { kind: emailKind, audience: emailAudience });
      setEmailDraft(r.data.draft);
    } catch (e) {
      setEmailDraft("Failed to draft: " + (e?.response?.data?.detail || e.message));
    } finally {
      setEmailBusy(false);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    load();
  };

  const openBundleBuilder = async () => {
    setBundleOpen(true);
    setBundleMsg("");
    setBundleForm({ title: "", price: 44, product_ids: [] });
    try {
      const r = await api.get("/admin/bundles/candidates");
      setBundleCandidates(r.data.products || []);
    } catch (e) {
      setBundleMsg("Could not load candidate products: " + (e?.response?.data?.detail || e.message));
    }
  };

  const toggleBundleChild = (id) => {
    setBundleForm((f) => {
      const cur = f.product_ids;
      if (cur.includes(id)) return { ...f, product_ids: cur.filter((x) => x !== id) };
      if (cur.length >= 3) return f; // cap at 3
      return { ...f, product_ids: [...cur, id] };
    });
  };

  const createBundle = async () => {
    if (!bundleForm.title.trim() || bundleForm.product_ids.length < 2) {
      setBundleMsg("Give the bundle a title and pick 2 or 3 products.");
      return;
    }
    setBundleBusy(true);
    setBundleMsg("");
    try {
      const r = await api.post("/admin/bundles", {
        title: bundleForm.title.trim(),
        price: Number(bundleForm.price) || 44,
        product_ids: bundleForm.product_ids,
        tags: ["bundle", "creator-starter"],
      });
      setBundleMsg(`Created bundle "${r.data.title}" @ $${r.data.price.toFixed(2)}`);
      setBundleForm({ title: "", price: 44, product_ids: [] });
      await load();
    } catch (e) {
      setBundleMsg("Bundle create failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBundleBusy(false);
    }
  };

  const sendEmail = async () => {
    if (!emailDraft || !emailSendTo.trim()) { setEmailSendMsg("Need draft + recipient"); return; }
    setEmailSendBusy(true); setEmailSendMsg("");
    // parse SUBJECT/BODY from draft
    const subjMatch = emailDraft.match(/SUBJECT:\s*(.+)/);
    const bodyMatch = emailDraft.match(/BODY:\s*([\s\S]+)/);
    const subject = subjMatch ? subjMatch[1].trim() : "A note from Meridian Press";
    const body = bodyMatch ? bodyMatch[1].trim() : emailDraft;
    try {
      await api.post("/admin/send-email", { to: emailSendTo, subject, body, is_html: false });
      setEmailSendMsg("Sent ✓");
    } catch (e) {
      setEmailSendMsg(e?.response?.data?.detail || "Send failed");
    } finally {
      setEmailSendBusy(false);
    }
  };

  const runAutoDrop = async () => {
    setDropBusy(true); setDropResult(null);
    const themes = dropThemes.split("\n").map((s) => s.trim()).filter(Boolean);
    try {
      const r = await api.post("/admin/auto-drop", {
        count: Number(dropCount), category: dropCategory,
        themes: themes.length ? themes : null, mark_featured: true,
        audience: "subscribers",
      });
      setDropResult(r.data);
      await load();
    } catch (e) {
      setDropResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setDropBusy(false);
    }
  };

  // Load email-status once on mount
  useEffect(() => {
    api.get("/admin/email-status").then((r) => setEmailConfigured(r.data?.configured)).catch(() => {});
  }, []);

  // Load abandoned-cart recovery state (only when Automations tab is active)
  const loadRecovery = useCallback(() => {
    api.get("/admin/recovery/pending")
      .then((r) => {
        setRecoveryPending(r.data?.pending || []);
        setRecoveryRecovered(r.data?.already_recovered || 0);
        setRecoveryStage2Pending(r.data?.stage2_pending || 0);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (tab === "automations") loadRecovery();
  }, [tab, loadRecovery]);

  const runRecovery = async () => {
    setRecoveryBusy(true); setRecoveryMsg("");
    try {
      const r = await api.post("/admin/recovery/run");
      setRecoveryMsg(`Sent ${r.data.sent} · scanned ${r.data.scanned}`);
      loadRecovery();
    } catch (e) {
      setRecoveryMsg(e?.response?.data?.detail || "Failed to run recovery.");
    } finally {
      setRecoveryBusy(false);
    }
  };

  if (!auth) return null;

  return (
    <div className="dash">
      {/* Sidebar */}
      <div className="flex">
        <aside className="w-60 min-h-screen border-r border-[#222] p-5 hidden lg:block">
          <div className="font-display font-medium text-2xl tracking-tight mb-1">Meridian Press</div>
          <div className="label-mono text-white/40 mb-8">Mission Control</div>
          <nav className="space-y-1">
            {[
              ["overview", "Overview", <Activity className="w-4 h-4" key="o" />],
              ["products", "Products", <Package className="w-4 h-4" key="p" />],
              ["research", "Research", <Zap className="w-4 h-4" key="rs" />],
              ["orders", "Orders", <ShoppingCart className="w-4 h-4" key="r" />],
              ["automations", "Automations", <Bot className="w-4 h-4" key="a" />],
              ["social", "Social Queue", <Sparkles className="w-4 h-4" key="q" />],
              ["schedule", "Schedule", <CalendarClock className="w-4 h-4" key="s" />],
              ["payouts", "Payouts", <CreditCard className="w-4 h-4" key="c" />],
            ].map(([k, label, icon]) => (
              <button
                key={k}
                data-testid={`tab-${k}`}
                onClick={() => setTab(k)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition ${tab === k ? "bg-[#1A1A1A] text-white" : "text-white/60 hover:bg-[#141414]"}`}
              >
                {icon}<span>{label}</span>
              </button>
            ))}
          </nav>
          <button data-testid="logout-btn" onClick={logout} className="mt-12 flex items-center gap-2 text-white/50 hover:text-white text-sm">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </aside>

        <main className="flex-1 p-6 sm:p-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
            <div>
              <div className="label-mono text-white/40">/ {tab.toUpperCase()}</div>
              <h1 className="font-display text-4xl font-black tracking-tighter mt-1">
                {tab === "overview" && "Today."}
                {tab === "products" && "Catalog."}
                {tab === "research" && "Product Intelligence."}
                {tab === "orders" && "Orders."}
                {tab === "automations" && "Automations."}
                {tab === "social" && "Social Queue."}
                {tab === "schedule" && "Schedule."}
                {tab === "payouts" && "Payouts."}
              </h1>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button data-testid="open-autodrop-btn" onClick={() => { setDropOpen(true); setDropResult(null); }} className="dash-btn inline-flex items-center gap-2">
                <Zap className="w-4 h-4" /> Auto-Drop
              </button>
              <button data-testid="open-generate-btn" onClick={() => setGenOpen(true)} className="dash-btn-ghost inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Generate Product
              </button>
              {tab === "products" && (
                <button data-testid="open-bundle-btn" onClick={openBundleBuilder} className="dash-btn-ghost inline-flex items-center gap-2">
                  <Package className="w-4 h-4" /> Create Bundle
                </button>
              )}
              <button data-testid="open-email-btn" onClick={() => { setEmailOpen(true); setEmailDraft(""); setEmailSendMsg(""); }} className="dash-btn-ghost inline-flex items-center gap-2">
                <Mail className="w-4 h-4" /> Draft Email
              </button>
            </div>
          </div>

          {tab === "overview" && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={<DollarSign className="w-4 h-4 text-[#FF3B30]" />} label="Revenue" value={`$${stats.revenue.toFixed(2)}`} testid="stat-revenue" />
                <StatCard icon={<TrendingUp className="w-4 h-4 text-[#FF3B30]" />} label="Profit (est)" value={`$${stats.profit.toFixed(2)}`} testid="stat-profit" sub="92% margin assumption" />
                <StatCard icon={<ShoppingCart className="w-4 h-4" />} label="Orders" value={stats.total_orders} testid="stat-orders" />
                <StatCard icon={<DollarSign className="w-4 h-4" />} label="AOV" value={`$${stats.aov.toFixed(2)}`} testid="stat-aov" />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={<Package className="w-4 h-4" />} label="Products" value={stats.total_products} testid="stat-products" />
                <StatCard icon={<Sparkles className="w-4 h-4 text-[#FF3B30]" />} label="AI products" value={stats.ai_products} testid="stat-ai-products" />
                <StatCard icon={<User className="w-4 h-4" />} label="Customers" value={stats.total_customers || 0} testid="stat-customers" />
                <StatCard icon={<AlertCircle className="w-4 h-4 text-yellow-500" />} label="Pending payment" value={stats.pending_orders || 0} testid="stat-pending" />
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={<Mail className="w-4 h-4" />} label="Email subscribers" value={stats.subscribers || 0} testid="stat-subscribers" />
                <StatCard icon={<ShoppingCart className="w-4 h-4 text-yellow-500" />} label="Abandoned carts" value={stats.abandoned_carts || 0} testid="stat-abandoned-carts" />
                <StatCard icon={<DollarSign className="w-4 h-4 text-[#4CE0B3]" />} label="Recovered revenue" value={`$${(stats.recovered_revenue || 0).toFixed(2)}`} testid="stat-recovered-revenue" sub="from abandoned-cart emails" />
                <StatCard icon={<Package className="w-4 h-4 text-white/50" />} label="Drafts to review" value={stats.draft_products || 0} testid="stat-drafts" />
              </div>

              <div className="grid lg:grid-cols-3 gap-4">
                <div className="dash-card p-5 lg:col-span-2" data-testid="revenue-7d">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display text-xl font-bold tracking-tight">Revenue · last 7 days</h3>
                    <span className="label-mono text-white/40">USD</span>
                  </div>
                  <Sparkbar data={stats.revenue_7d || []} />
                </div>
                <div className="dash-card p-5" data-testid="top-products">
                  <h3 className="font-display text-xl font-bold tracking-tight mb-3">Top products</h3>
                  {(stats.top_products || []).length === 0 && <div className="text-sm text-white/40 font-mono-ui">No sales yet.</div>}
                  <ul className="space-y-2">
                    {(stats.top_products || []).map((t) => (
                      <li key={t.product_id} className="flex items-center justify-between text-sm">
                        <span className="font-display truncate pr-2">{t.title}</span>
                        <span className="font-mono-ui text-white/60">{t.units}× · ${t.revenue.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {(stats.insights || []).length > 0 && (
                <div className="dash-card p-5" data-testid="insights-card">
                  <h3 className="font-display text-xl font-bold tracking-tight mb-3">What should I work on next?</h3>
                  <ul className="space-y-2">
                    {stats.insights.map((ins, i) => (
                      <li key={i} data-testid={`insight-${i}`} className="text-sm text-white/70 flex gap-2">
                        <span style={{ color: "#FF3B30" }}>→</span>{ins}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <ActivityFeed activity={activity} />
            </div>
          )}

          {tab === "products" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => (
                <div key={p.id} data-testid={`admin-product-${p.id}`} className="dash-card overflow-hidden">
                  <div className="aspect-square bg-[#0A0A0A] relative">
                    <img src={productImage(p)} alt="" className="w-full h-full object-cover opacity-90" />
                    {!p.published && (
                      <span className="absolute top-2 left-2 px-2 py-1 label-mono text-xs" style={{ background: "#FF3B30", color: "#ffffff" }}>DRAFT</span>
                    )}
                    {p.is_bundle && (
                      <span className="absolute top-2 right-2 px-2 py-1 label-mono text-xs" style={{ background: "#2563EB", color: "#ffffff" }}>BUNDLE · {(p.bundle_product_ids || []).length}</span>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="font-display font-bold truncate">{p.title}</div>
                      {p.ai_generated && <span className="label-mono text-[#FF3B30]">/AI</span>}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="label-mono text-white/50">{p.category}</span>
                      <span className="font-mono-ui">${p.price.toFixed(2)}</span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                      <span data-testid={`collection-${p.id}`} className="px-1.5 py-0.5 rounded label-mono text-[10px]" style={{ background: "#1A1A1A", color: "#8a8b8f" }}>
                        {COLLECTION_MAP[p.category] || p.category}
                      </span>
                      {p.price_tier && (
                        <span data-testid={`price-tier-${p.id}`} className="px-1.5 py-0.5 rounded label-mono text-[10px]" style={{ background: "#1A1A1A", color: "#4CE0B3" }}>
                          {p.price_tier}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: p.download_url ? "#4CE0B3" : "rgba(255,255,255,0.4)" }}>
                      <span>{p.download_url ? "● Download set" : "○ No file"}</span>
                      {p.sales_count > 0 && <span className="ml-auto label-mono">{p.sales_count} sold</span>}
                    </div>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <button
                        data-testid={`publish-${p.id}`}
                        onClick={() => {
                          api.patch(`/admin/products/${p.id}`, { published: !p.published }).then(load);
                        }}
                        className="dash-btn-ghost inline-flex items-center gap-1 text-xs"
                        style={p.published ? {} : { background: "#4CE0B3", color: "#0A0A0A" }}
                      >
                        {p.published ? "Unpublish" : "Publish"}
                      </button>
                      <button data-testid={`edit-dl-${p.id}`} onClick={() => {
                        const cur = p.download_url || "";
                        const url = window.prompt(`Download URL for "${p.title}"\n(Paste a public link — Dropbox, Google Drive, Gumroad, S3, etc. — or an existing internal path like /api/download/...)`, cur);
                        if (url === null) return;
                        api.patch(`/admin/products/${p.id}`, { download_url: url.trim() }).then(load);
                      }} className="dash-btn-ghost inline-flex items-center gap-1 text-xs">
                        📎 Set file
                      </button>
                      <button data-testid={`delete-product-${p.id}`} onClick={() => deleteProduct(p.id)} className="dash-btn-ghost inline-flex items-center gap-1 text-xs">
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "orders" && (
            <div className="dash-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-white/50 label-mono border-b border-[#222]">
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Items</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-white/40 font-mono-ui text-sm">No orders yet.</td></tr>
                  )}
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-b border-[#222] hover:bg-[#1A1A1A]">
                      <td className="px-4 py-3 font-mono-ui text-xs text-white/60">{new Date(o.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <div className="font-display font-semibold">{o.customer_name}</div>
                        <div className="text-xs text-white/50">{o.customer_email}</div>
                      </td>
                      <td className="px-4 py-3 font-mono-ui text-xs">{o.items.length}</td>
                      <td className="px-4 py-3 font-mono-ui">${o.total.toFixed(2)}</td>
                      <td className="px-4 py-3"><span className="label-mono text-[#FF3B30]">{o.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "automations" && (
            <div className="space-y-4">
              <IntegrationsPanel />
              <PrintfulSyncCard />
              <div className="grid lg:grid-cols-2 gap-4">
                <AutomationCard
                  title="Auto-Drop"
                  desc="One click → generate N products, draft announcement email, log activity."
                  status="ACTIVE"
                  onAction={() => { setDropOpen(true); setDropResult(null); }}
                  actionLabel="Run drop"
                />
                <AutomationCard
                  title="AI Product Generation"
                  desc="Create a single new product with AI copy + AI image (Nano Banana)."
                  status="ACTIVE"
                  onAction={() => setGenOpen(true)}
                actionLabel="Generate now"
              />
              <AutomationCard
                title="Niche Kit Generator"
                desc="One niche → 50 AI prompts (customizable), 3D mockup, PDF, featured product. ~60-120s."
                status="ACTIVE"
                onAction={() => setNicheOpen(true)}
                actionLabel="Generate niche kit"
              />
              <AutomationCard
                title="AI Customer Support"
                desc="Streaming chatbot answers customer questions on the storefront 24/7."
                status="ACTIVE"
              />
              <AutomationCard
                title="AI Email Drafting + Send"
                desc="Draft welcome, abandoned cart, promo emails. Send via Resend in one click."
                status={emailConfigured ? "ACTIVE" : "DRAFT ONLY"}
                onAction={() => { setEmailOpen(true); setEmailDraft(""); setEmailSendMsg(""); }}
                actionLabel="Draft & send"
              />
              <RecoveryCard
                pending={recoveryPending}
                recovered={recoveryRecovered}
                stage2Pending={recoveryStage2Pending}
                busy={recoveryBusy}
                msg={recoveryMsg}
                onRun={runRecovery}
                onRefresh={loadRecovery}
                emailConfigured={emailConfigured}
              />
              <AutomationCard
                title="Customer Accounts"
                desc="Email/password + Google sign-in with secure httpOnly cookie sessions."
                status="ACTIVE"
              />
              <AutomationCard
                title="Activity Logging"
                desc="Every AI action is logged to your mission control feed."
                status="ACTIVE"
              />
              </div>
            </div>
          )}

          {tab === "schedule" && <ScheduleTab />}

          {tab === "research" && <ResearchTab />}

          {tab === "social" && <SocialQueueTab />}

          {tab === "payouts" && <PayoutsTab stats={stats} />}
        </main>
      </div>

      {genOpen && (
        <Modal title="Generate Product with AI" onClose={() => setGenOpen(false)}>
          <p className="text-sm text-white/60 mb-4">Describe the vibe. AI writes the copy & creates the image.</p>
          <label className="block mb-3">
            <span className="label-mono text-white/60">Theme / idea</span>
            <input data-testid="gen-theme" value={genTheme} onChange={(e) => setGenTheme(e.target.value)} placeholder="e.g. minimalist mountain at dawn" className="dash-input mt-2" />
          </label>
          <label className="block mb-4">
            <span className="label-mono text-white/60">Category</span>
            <select data-testid="gen-category" value={genCategory} onChange={(e) => setGenCategory(e.target.value)} className="dash-input mt-2">
              <option>Print</option>
              <option>Digital</option>
              <option>Apparel</option>
            </select>
          </label>
          <button data-testid="gen-submit" onClick={generate} disabled={genBusy || !genTheme.trim()} className="dash-btn w-full inline-flex items-center justify-center gap-2">
            {genBusy ? "Generating (20-40s)…" : <><Sparkles className="w-4 h-4" /> Generate</>}
          </button>
        </Modal>
      )}

      {bundleOpen && (
        <Modal title="Create Bundle" onClose={() => setBundleOpen(false)}>
          <p className="text-sm text-white/60 mb-4">
            Pick 2 or 3 products to group as a Creator Starter Bundle. On paid checkout, buyers get every child product&rsquo;s downloads.
          </p>
          <label className="block mb-3">
            <span className="label-mono text-white/60">Bundle title</span>
            <input
              data-testid="bundle-title"
              value={bundleForm.title}
              onChange={(e) => setBundleForm({ ...bundleForm, title: e.target.value })}
              placeholder="e.g. Creator Starter Bundle · 3-Pack"
              className="dash-input mt-2"
            />
          </label>
          <label className="block mb-4">
            <span className="label-mono text-white/60">Bundle price (USD)</span>
            <input
              data-testid="bundle-price"
              type="number"
              step="1"
              min="1"
              value={bundleForm.price}
              onChange={(e) => setBundleForm({ ...bundleForm, price: e.target.value })}
              className="dash-input mt-2"
            />
          </label>
          <div className="mb-2 label-mono text-white/60">
            Pick 2-3 products ({bundleForm.product_ids.length}/3)
          </div>
          <div className="max-h-64 overflow-auto border border-[#222] rounded-md mb-4" data-testid="bundle-candidates">
            {bundleCandidates.length === 0 && (
              <div className="p-3 text-sm text-white/40 font-mono-ui">No candidate products with a download file yet.</div>
            )}
            {bundleCandidates.map((c) => {
              const active = bundleForm.product_ids.includes(c.id);
              return (
                <button
                  key={c.id}
                  data-testid={`bundle-pick-${c.id}`}
                  onClick={() => toggleBundleChild(c.id)}
                  className="w-full text-left flex items-center gap-3 px-3 py-2 border-b border-[#222] hover:bg-[#181818]"
                  style={{ background: active ? "#1e293b" : "transparent" }}
                >
                  <span className="w-4 h-4 rounded border border-white/40 flex items-center justify-center text-xs" style={{ background: active ? "#2563EB" : "transparent" }}>
                    {active ? "✓" : ""}
                  </span>
                  <span className="flex-1 truncate">
                    <span className="font-display font-semibold text-sm">{c.title}</span>
                    <span className="ml-2 label-mono text-white/40">{c.category}</span>
                  </span>
                  <span className="font-mono-ui text-sm text-white/70">${(c.price || 0).toFixed(2)}</span>
                </button>
              );
            })}
          </div>
          {bundleMsg && <div data-testid="bundle-msg" className="text-sm mb-3" style={{ color: bundleMsg.startsWith("Created") ? "#4CE0B3" : "#FF3B30" }}>{bundleMsg}</div>}
          <button
            data-testid="bundle-submit"
            onClick={createBundle}
            disabled={bundleBusy || !bundleForm.title.trim() || bundleForm.product_ids.length < 2}
            className="dash-btn w-full inline-flex items-center justify-center gap-2"
          >
            {bundleBusy ? "Creating…" : <><Package className="w-4 h-4" /> Create bundle</>}
          </button>
        </Modal>
      )}

      {nicheOpen && (
        <Modal title="Niche Kit Generator" onClose={() => setNicheOpen(false)}>
          <p className="text-sm text-white/60 mb-4">
            Type an audience niche. AI writes 3 sections of prompts (default 20 + 20 + 10 = 50 total), renders a 3D mockup, builds a PDF, and publishes as a featured product. ~60-120s.
          </p>
          <label className="block mb-3">
            <span className="label-mono text-white/60">Audience niche</span>
            <input
              data-testid="niche-input"
              value={nicheForm.niche}
              onChange={(e) => setNicheForm({ ...nicheForm, niche: e.target.value })}
              placeholder="e.g. Fitness Coaches / SaaS Founders / Wedding Planners"
              className="dash-input mt-2"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="label-mono text-white/60">Kit type</span>
              <input
                data-testid="niche-kit-type"
                value={nicheForm.kit_type}
                onChange={(e) => setNicheForm({ ...nicheForm, kit_type: e.target.value })}
                placeholder="Prompt Kit"
                className="dash-input mt-2"
              />
            </label>
            <label className="block">
              <span className="label-mono text-white/60">Price ($)</span>
              <input
                data-testid="niche-price"
                type="number"
                min="0"
                step="1"
                value={nicheForm.price}
                onChange={(e) => setNicheForm({ ...nicheForm, price: e.target.value })}
                className="dash-input mt-2"
              />
            </label>
          </div>
          <div className="mt-4 space-y-2">
            <div className="label-mono text-white/40 text-[10px]">SECTIONS · advanced</div>
            {["a", "b", "c"].map((k, i) => (
              <div key={k} className="grid grid-cols-[1fr_80px] gap-2">
                <input
                  data-testid={`niche-section-${k}-label`}
                  value={nicheForm[`section_${k}_label`]}
                  onChange={(e) => setNicheForm({ ...nicheForm, [`section_${k}_label`]: e.target.value })}
                  placeholder={`Section ${i + 1} label`}
                  className="dash-input"
                />
                <input
                  data-testid={`niche-section-${k}-count`}
                  type="number"
                  min="1"
                  max="50"
                  value={nicheForm[`section_${k}_count`]}
                  onChange={(e) => setNicheForm({ ...nicheForm, [`section_${k}_count`]: e.target.value })}
                  className="dash-input"
                />
              </div>
            ))}
            <label className="flex items-center gap-2 mt-3 text-sm text-white/70">
              <input
                data-testid="niche-featured"
                type="checkbox"
                checked={nicheForm.featured}
                onChange={(e) => setNicheForm({ ...nicheForm, featured: e.target.checked })}
              />
              Mark as featured (top of homepage grid)
            </label>
          </div>
          <button
            data-testid="niche-submit"
            onClick={runNicheKit}
            disabled={nicheBusy || !nicheForm.niche.trim()}
            className="dash-btn w-full inline-flex items-center justify-center gap-2 mt-4"
          >
            {nicheBusy ? "Queuing…" : <><Sparkles className="w-4 h-4" /> Generate niche kit</>}
          </button>
        </Modal>
      )}

      {emailOpen && (
        <Modal title="Draft Email with AI" onClose={() => setEmailOpen(false)}>
          <label className="block mb-3">
            <span className="label-mono text-white/60">Type</span>
            <select data-testid="email-kind" value={emailKind} onChange={(e) => setEmailKind(e.target.value)} className="dash-input mt-2">
              <option value="welcome">Welcome</option>
              <option value="abandoned_cart">Abandoned cart</option>
              <option value="promo">Promo</option>
            </select>
          </label>
          <label className="block mb-3">
            <span className="label-mono text-white/60">Audience</span>
            <input data-testid="email-audience" value={emailAudience} onChange={(e) => setEmailAudience(e.target.value)} className="dash-input mt-2" />
          </label>
          <button data-testid="email-draft-btn" onClick={draftEmail} disabled={emailBusy} className="dash-btn w-full inline-flex items-center justify-center gap-2">
            {emailBusy ? "Drafting…" : <><Mail className="w-4 h-4" /> Draft</>}
          </button>
          {emailDraft && (
            <>
              <pre data-testid="email-draft-output" className="mt-4 bg-[#0A0A0A] border border-[#222] rounded p-3 text-xs text-white/80 whitespace-pre-wrap font-mono-ui max-h-60 overflow-y-auto">
                {emailDraft}
              </pre>
              <div className="mt-4 border-t border-[#222] pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="label-mono text-white/60">Send via Resend</span>
                  <span className={`label-mono ${emailConfigured ? "text-[#FF3B30]" : "text-white/40"}`}>
                    {emailConfigured ? "● CONFIGURED" : "○ NO API KEY"}
                  </span>
                </div>
                <div className="flex gap-2">
                  <input
                    data-testid="email-send-to"
                    type="email"
                    value={emailSendTo}
                    onChange={(e) => setEmailSendTo(e.target.value)}
                    placeholder="recipient@example.com"
                    className="dash-input flex-1"
                  />
                  <button
                    data-testid="email-send-btn"
                    onClick={sendEmail}
                    disabled={emailSendBusy || !emailConfigured}
                    title={emailConfigured ? "Send via Resend" : "Add RESEND_API_KEY in backend/.env"}
                    className="dash-btn inline-flex items-center gap-1"
                  >
                    {emailSendBusy ? "Sending…" : <><Send className="w-3.5 h-3.5" /> Send</>}
                  </button>
                </div>
                {emailSendMsg && <div data-testid="email-send-msg" className="mt-2 font-mono-ui text-xs text-white/70">{emailSendMsg}</div>}
                {!emailConfigured && (
                  <div className="mt-2 font-mono-ui text-xs text-white/40">
                    Add <code>RESEND_API_KEY=re_...</code> to <code>/app/backend/.env</code> then restart backend.
                  </div>
                )}
              </div>
            </>
          )}
        </Modal>
      )}

      {dropOpen && (
        <Modal title="Run Auto-Drop" onClose={() => setDropOpen(false)}>
          <p className="text-sm text-white/60 mb-4">
            One click → generate N AI products + draft a "new drop" announcement email + log everything.
          </p>
          <label className="block mb-3">
            <span className="label-mono text-white/60">How many products</span>
            <input data-testid="drop-count" type="number" min={1} max={6} value={dropCount}
              onChange={(e) => setDropCount(e.target.value)} className="dash-input mt-2" />
          </label>
          <label className="block mb-3">
            <span className="label-mono text-white/60">Category</span>
            <select data-testid="drop-category" value={dropCategory} onChange={(e) => setDropCategory(e.target.value)} className="dash-input mt-2">
              <option>Print</option>
              <option>Digital</option>
              <option>Apparel</option>
            </select>
          </label>
          <label className="block mb-4">
            <span className="label-mono text-white/60">Themes (one per line — leave blank for auto)</span>
            <textarea data-testid="drop-themes" rows={3} value={dropThemes}
              onChange={(e) => setDropThemes(e.target.value)}
              placeholder="e.g. neon city rain at 4am&#10;japanese ink mountain"
              className="dash-input mt-2 font-mono-ui" />
          </label>
          <button data-testid="drop-submit" onClick={runAutoDrop} disabled={dropBusy}
            className="dash-btn w-full inline-flex items-center justify-center gap-2">
            {dropBusy ? `Running drop (${dropCount * 30}-${dropCount * 60}s)…` : <><Zap className="w-4 h-4" /> Launch Drop</>}
          </button>

          {dropResult && (
            <div className="mt-5 border-t border-[#222] pt-4 space-y-3">
              {dropResult.error ? (
                <div data-testid="drop-error" className="text-sm text-[#FF3B30] font-mono-ui">{dropResult.error}</div>
              ) : (
                <>
                  <div data-testid="drop-summary" className="label-mono text-[#FF3B30]">
                    ✓ {dropResult.products.length} products generated · email drafted
                  </div>
                  <ul className="space-y-1 text-sm">
                    {dropResult.products.map((p) => (
                      <li key={p.id} className="font-display">— {p.title} <span className="font-mono-ui text-xs text-white/50">${p.price.toFixed(2)}</span></li>
                    ))}
                  </ul>
                  {dropResult.email_draft && (
                    <pre className="bg-[#0A0A0A] border border-[#222] rounded p-3 text-xs text-white/70 whitespace-pre-wrap font-mono-ui max-h-48 overflow-y-auto">
                      {dropResult.email_draft}
                    </pre>
                  )}
                </>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, testid, sub }) {
  return (
    <div className="dash-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between text-white/50 label-mono">{label} {icon}</div>
      <div className="mt-2 font-display text-3xl font-black tracking-tighter">{value}</div>
      {sub && <div className="mt-1 font-mono-ui text-[10px] text-white/40">{sub}</div>}
    </div>
  );
}

function Sparkbar({ data }) {
  const max = Math.max(1, ...data.map((d) => d.revenue));
  return (
    <div className="flex items-end gap-2 h-32">
      {data.map((d) => {
        const h = Math.max(2, Math.round((d.revenue / max) * 100));
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full bg-[#FF3B30]/80 hover:bg-[#FF3B30] transition" style={{ height: `${h}%` }} title={`$${d.revenue.toFixed(2)}`} />
            <span className="font-mono-ui text-[10px] text-white/40">{d.date.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
}

function ActivityFeed({ activity }) {
  return (
    <div className="dash-card p-5" data-testid="activity-feed">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><span className="ai-dot" /><h3 className="font-display text-xl font-bold tracking-tight">Live activity</h3></div>
        <span className="label-mono text-white/40">{activity.length} events</span>
      </div>
      <div className="space-y-1 max-h-80 overflow-y-auto">
        {activity.length === 0 && <div className="text-sm text-white/40 font-mono-ui">No activity yet.</div>}
        {activity.map((a) => (
          <div key={a.id} className="activity-row flex items-start gap-3 py-2 border-b border-[#1A1A1A]">
            <span className="label-mono text-[#FF3B30] w-24 flex-shrink-0">{a.type}</span>
            <span className="text-sm text-white/80 flex-1">{a.message}</span>
            <span className="font-mono-ui text-xs text-white/40">{new Date(a.created_at).toLocaleTimeString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AutomationCard({ title, desc, status, onAction, actionLabel }) {
  return (
    <div className="dash-card p-6">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold tracking-tight">{title}</h3>
        <span className="label-mono text-[#FF3B30] inline-flex items-center gap-2"><span className="ai-dot" />{status}</span>
      </div>
      <p className="mt-2 text-sm text-white/60">{desc}</p>
      {onAction && (
        <button onClick={onAction} className="dash-btn-ghost mt-4">{actionLabel} →</button>
      )}
    </div>
  );
}

function RecoveryCard({ pending, recovered, stage2Pending, busy, msg, onRun, onRefresh, emailConfigured }) {
  const pendingCount = pending.length;
  const potentialRevenue = pending.reduce((s, p) => s + Number(p.total || 0), 0);
  return (
    <div className="dash-card p-6" data-testid="recovery-card">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold tracking-tight">Abandoned-Cart Recovery</h3>
        <span className="label-mono inline-flex items-center gap-2" style={{ color: emailConfigured ? "#FF3B30" : "#8a8b8f" }}>
          <span className="ai-dot" />{emailConfigured ? (pendingCount > 0 ? "READY TO SEND" : "IDLE") : "NEEDS RESEND KEY"}
        </span>
      </div>
      <p className="mt-2 text-sm text-white/60">
        2-message sequence: reminder email after 60 min, benefit-led follow-up after 48h if still unpaid — both scanned every 15 min.
      </p>
      <div className="mt-4 grid grid-cols-4 gap-3 text-center">
        <div className="dash-card p-3">
          <div className="label-mono text-white/50">PENDING</div>
          <div data-testid="recovery-pending-count" className="font-display text-2xl mt-1">{pendingCount}</div>
        </div>
        <div className="dash-card p-3">
          <div className="label-mono text-white/50">POTENTIAL</div>
          <div className="font-display text-2xl mt-1">${potentialRevenue.toFixed(0)}</div>
        </div>
        <div className="dash-card p-3">
          <div className="label-mono text-white/50">FOLLOW-UP DUE</div>
          <div data-testid="recovery-stage2-count" className="font-display text-2xl mt-1">{stage2Pending || 0}</div>
        </div>
        <div className="dash-card p-3">
          <div className="label-mono text-white/50">EMAILED</div>
          <div data-testid="recovery-recovered-count" className="font-display text-2xl mt-1">{recovered}</div>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button
          data-testid="recovery-run-btn"
          onClick={onRun}
          disabled={busy || !emailConfigured || (pendingCount === 0 && !stage2Pending)}
          className="dash-btn-ghost"
        >
          {busy ? "Sending…" : `Send ${pendingCount + (stage2Pending || 0)} email${pendingCount + (stage2Pending || 0) === 1 ? "" : "s"} now →`}
        </button>
        <button onClick={onRefresh} className="dash-btn-ghost">Refresh</button>
      </div>
      {msg && <div data-testid="recovery-msg" className="mt-3 text-xs" style={{ color: "#FF3B30" }}>{msg}</div>}
    </div>
  );
}


function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="dash-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl font-bold tracking-tight">{title}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}


const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function ScheduleTab() {
  const [cfg, setCfg] = useState({ enabled: false, frequency: "weekly", day_of_week: 1, hour_utc: 9, count: 3, category: "Print", categories: [], audience: "subscribers" });
  const [allCats, setAllCats] = useState(["Print", "Digital", "Apparel"]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/admin/schedule").then((r) => setCfg({ frequency: "weekly", categories: [], ...r.data })).catch(() => {});
    api.get("/categories").then((r) => setAllCats(r.data?.categories || [])).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await api.post("/admin/schedule", cfg);
      setMsg("Saved ✓");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const toggleCat = (c) => {
    const has = (cfg.categories || []).includes(c);
    setCfg({ ...cfg, categories: has ? cfg.categories.filter((x) => x !== c) : [...(cfg.categories || []), c] });
  };

  const runsPerDay = cfg.frequency === "every_2h" ? 12 : cfg.frequency === "daily" ? 1 : 1;
  const itemsPerDay = runsPerDay * cfg.count;
  const cadenceText = !cfg.enabled ? "Disabled"
    : cfg.frequency === "every_2h" ? `Every 2 hours · ${cfg.count} item(s) per run · ~${itemsPerDay}/day`
    : cfg.frequency === "daily" ? `Daily @ ${String(cfg.hour_utc).padStart(2, "0")}:00 UTC · ${cfg.count}/day`
    : `Every ${DAYS[cfg.day_of_week]} @ ${String(cfg.hour_utc).padStart(2, "0")}:00 UTC · ${cfg.count}/week`;
  const catList = (cfg.categories && cfg.categories.length) ? cfg.categories.join(" · ") : cfg.category;

  return (
    <div className="dash-card p-6 max-w-3xl" data-testid="schedule-tab">
      <h3 className="font-display text-2xl font-bold tracking-tight">Recurring auto-drop</h3>
      <p className="text-sm text-white/60 mt-1">Runs in the background — generates AI products + drafts a "new drop" email. Categories rotate.</p>

      <label className="flex items-center gap-3 mt-6 cursor-pointer">
        <input data-testid="schedule-enabled" type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="w-5 h-5 accent-[#B14D2A]" />
        <span className="label-mono text-white/80">Enabled</span>
      </label>

      <div className="mt-5">
        <span className="label-mono text-white/60">Frequency</span>
        <div className="mt-2 inline-flex rounded-full overflow-hidden border" style={{ borderColor: "#25272B" }}>
          {[["every_2h", "Every 2h"], ["daily", "Daily"], ["weekly", "Weekly"]].map(([f, label]) => (
            <button
              key={f}
              data-testid={`schedule-freq-${f}`}
              type="button"
              onClick={() => setCfg({ ...cfg, frequency: f })}
              className="px-5 py-2 text-sm font-medium transition"
              style={{
                background: cfg.frequency === f ? "var(--accent)" : "transparent",
                color: cfg.frequency === f ? "#fff" : "rgba(255,255,255,0.7)",
              }}
            >{label}</button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        {cfg.frequency === "weekly" && (
          <label className="block">
            <span className="label-mono text-white/60">Day of week</span>
            <select data-testid="schedule-day" value={cfg.day_of_week} onChange={(e) => setCfg({ ...cfg, day_of_week: Number(e.target.value) })} className="dash-input mt-2">
              {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </label>
        )}
        {cfg.frequency !== "every_2h" && (
          <label className="block">
            <span className="label-mono text-white/60">Hour (UTC, 0–23)</span>
            <input data-testid="schedule-hour" type="number" min={0} max={23} value={cfg.hour_utc} onChange={(e) => setCfg({ ...cfg, hour_utc: Number(e.target.value) })} className="dash-input mt-2" />
          </label>
        )}
        <label className="block">
          <span className="label-mono text-white/60">Items per run</span>
          <input data-testid="schedule-count" type="number" min={1} max={6} value={cfg.count} onChange={(e) => setCfg({ ...cfg, count: Number(e.target.value) })} className="dash-input mt-2" />
        </label>
      </div>

      <div className="mt-6">
        <span className="label-mono text-white/60">Categories in rotation (pick 1+; each run picks the next one)</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {allCats.map((c) => {
            const active = (cfg.categories || []).includes(c);
            return (
              <button
                key={c}
                data-testid={`schedule-cat-${c}`}
                type="button"
                onClick={() => toggleCat(c)}
                className="px-3 py-1.5 rounded-full text-xs font-medium transition border"
                style={{
                  background: active ? "var(--accent)" : "transparent",
                  color: active ? "#fff" : "rgba(255,255,255,0.75)",
                  borderColor: active ? "var(--accent)" : "#25272B",
                }}
              >{c}</button>
            );
          })}
        </div>
        <p className="mt-2 font-mono-ui text-[10px] text-white/40">Leave all off to use the single-category legacy mode.</p>
      </div>

      <button data-testid="schedule-save" onClick={save} disabled={saving} className="dash-btn mt-6">{saving ? "Saving…" : "Save schedule"}</button>
      {msg && <span data-testid="schedule-msg" className="ml-3 font-mono-ui text-sm text-white/60">{msg}</span>}

      <p className="mt-6 text-xs text-white/40 font-mono-ui">
        Current: {cadenceText}{cfg.enabled ? ` · rotates → ${catList}` : ""}
      </p>

      <NicheScheduleCard />
    </div>
  );
}

// ─── Weekly Niche Kit Schedule (one niche per weekday) ───
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function NicheScheduleCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/admin/niche-schedule").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const setSlot = (i, patch) => {
    setCfg({ ...cfg, slots: cfg.slots.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  };

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await api.put("/admin/niche-schedule", cfg);
      setMsg("Saved ✓");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const [runBusyIdx, setRunBusyIdx] = useState(null);
  const runNow = async (i) => {
    setRunBusyIdx(i); setMsg("");
    try {
      const r = await api.post(`/admin/niche-schedule/run/${i}`);
      setMsg(r?.data?.message || `Fired slot ${i} — appears in ~60-120s.`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Run failed");
    } finally { setRunBusyIdx(null); }
  };

  if (!cfg) return null;
  const active = cfg.slots.filter((s) => (s.niche || "").trim()).length;

  return (
    <div className="dash-card p-6 mt-6" data-testid="niche-schedule-card">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-display text-xl font-bold tracking-tight">Weekly Niche Kit Schedule</h3>
          <p className="text-sm text-white/60 mt-1">
            One niche kit per weekday. Each slot triggers the same premium pipeline (50 prompts + 3D mockup + PDF + featured product) at the configured hour.
          </p>
        </div>
        <label className="flex items-center gap-3 text-sm text-white/70">
          <span className="label-mono text-white/60">Enabled</span>
          <input
            data-testid="niche-schedule-enabled"
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
          />
        </label>
      </div>

      <div className="grid sm:grid-cols-[100px_1fr] gap-4 items-center mb-4">
        <span className="label-mono text-white/60">Run at (UTC hour)</span>
        <select
          data-testid="niche-schedule-hour"
          value={cfg.hour_utc}
          onChange={(e) => setCfg({ ...cfg, hour_utc: Number(e.target.value) })}
          className="dash-input w-24"
        >
          {Array.from({ length: 24 }).map((_, h) => (
            <option key={h} value={h}>{String(h).padStart(2, "0")}:00 UTC</option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        {cfg.slots.map((slot, i) => (
          <div
            key={i}
            data-testid={`niche-slot-${i}`}
            className="grid grid-cols-[42px_1fr_120px_70px_90px] gap-2 items-center"
          >
            <span className="label-mono text-white/60 text-center">{WEEKDAYS[i]}</span>
            <input
              data-testid={`niche-slot-${i}-niche`}
              value={slot.niche}
              placeholder={`Niche for ${WEEKDAYS[i]}… (leave blank to skip)`}
              onChange={(e) => setSlot(i, { niche: e.target.value })}
              className="dash-input"
            />
            <input
              data-testid={`niche-slot-${i}-kit-type`}
              value={slot.kit_type}
              placeholder="Prompt Kit"
              onChange={(e) => setSlot(i, { kit_type: e.target.value })}
              className="dash-input"
            />
            <input
              data-testid={`niche-slot-${i}-price`}
              type="number"
              min="0"
              step="1"
              value={slot.price}
              onChange={(e) => setSlot(i, { price: Number(e.target.value) || 0 })}
              className="dash-input"
            />
            <button
              data-testid={`niche-slot-${i}-run-now`}
              onClick={() => runNow(i)}
              disabled={runBusyIdx !== null || !slot.niche.trim()}
              className="dash-btn-ghost text-xs whitespace-nowrap"
              title="Fire this niche kit immediately"
            >
              {runBusyIdx === i ? "Firing…" : "Run now"}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <button
          data-testid="niche-schedule-save"
          onClick={save}
          disabled={saving}
          className="dash-btn"
        >
          {saving ? "Saving…" : "Save schedule"}
        </button>
        {msg && (
          <span data-testid="niche-schedule-msg" className="font-mono-ui text-sm text-white/60">
            {msg}
          </span>
        )}
        <span className="ml-auto label-mono text-white/40 text-[10px]">
          {active}/7 slots active · {cfg.enabled ? "SCHEDULE ON" : "OFF"} · fires at {String(cfg.hour_utc).padStart(2, "0")}:00 UTC
        </span>
      </div>
    </div>
  );
}

function PayoutsTab({ stats }) {
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="dash-card p-6" data-testid="payouts-summary">
        <h3 className="font-display text-2xl font-bold tracking-tight">Where your money goes</h3>
        <p className="text-sm text-white/60 mt-1">
          Meridian Press uses <strong>Stripe</strong> for payments. Stripe deposits your sales directly into your bank account on a rolling 2–7 day schedule. You don't withdraw manually — it's automatic.
        </p>
        <div className="grid sm:grid-cols-3 gap-4 mt-5">
          <StatCard icon={<DollarSign className="w-4 h-4 text-[#FF3B30]" />} label="Total revenue" value={`$${stats.revenue.toFixed(2)}`} testid="payouts-revenue" />
          <StatCard icon={<TrendingUp className="w-4 h-4 text-[#FF3B30]" />} label="Est. profit" value={`$${stats.profit.toFixed(2)}`} testid="payouts-profit" sub="after ~2.9% + $0.30 PSP fees" />
          <StatCard icon={<AlertCircle className="w-4 h-4 text-yellow-500" />} label="Pending payment" value={stats.pending_orders || 0} testid="payouts-pending" />
        </div>
      </div>

      <div className="dash-card p-6">
        <h3 className="font-display text-xl font-bold tracking-tight">How to actually get paid</h3>
        <ol className="mt-3 space-y-3 text-sm list-decimal pl-5">
          <li>Create a free Stripe account at <a className="text-[#FF3B30] underline" href="https://dashboard.stripe.com/register" target="_blank" rel="noreferrer">dashboard.stripe.com/register</a>.</li>
          <li>Add a bank account in Stripe → <em>Settings → Bank accounts and scheduling</em>. Verify identity (KYC, ~5 min).</li>
          <li>Copy your <strong>live</strong> secret key. After you deploy to production, paste it as <code className="text-[#FF3B30]">STRIPE_API_KEY</code> in the deployment env vars.</li>
          <li>(Optional) Enable Apple Pay, Google Pay, Klarna, Cash App, and Link in Stripe → <em>Settings → Payment methods</em>. Zero code on your side.</li>
          <li>Stripe automatically pays out to your bank every 2–7 days. View payouts in the Stripe dashboard.</li>
        </ol>
      </div>

      <div className="dash-card p-6">
        <h3 className="font-display text-xl font-bold tracking-tight">Stripe environment</h3>
        <p className="text-sm text-white/60 mt-1">
          Test-mode checkouts use test cards (try <code className="text-[#FF3B30]">4242 4242 4242 4242</code>, any future date, any CVC). No real money moves until <code className="text-[#FF3B30]">STRIPE_API_KEY</code> is switched to a <code className="text-[#FF3B30]">sk_live_…</code> key in the production env vars (then Redeploy).
        </p>
      </div>

      <div className="dash-card p-6">
        <h3 className="font-display text-xl font-bold tracking-tight">Deploy to production</h3>
        <p className="text-sm text-white/60 mt-1">
          Read <code className="text-[#FF3B30]">/app/DEPLOY.md</code> for the full walkthrough. Short version: click the <strong>Deploy</strong> button in the Emergent toolbar → <em>Deploy Now</em> → wait ~10 min → set production env vars (STRIPE_API_KEY=sk_live_..., RESEND_API_KEY, JWT_SECRET, OWNER_PASSWORD, CORS_ORIGINS=your-prod-domain). ~50 credits / month.
        </p>
      </div>
    </div>
  );
}

// ─── Product Intelligence: AI Research Analyst ───
const OPP_STATUS_COLOR = {
  RESEARCHING: "#8a8b8f", WATCH: "#eab308", BUILD: "#4CE0B3",
  "RESEARCH MORE": "#8a8b8f", REJECTED: "#FF3B30",
  "IN DEVELOPMENT": "#2563EB", DRAFT: "#eab308", PUBLISHED: "#4CE0B3",
};

function ResearchTab() {
  const [opps, setOpps] = useState([]);
  const [nextBest, setNextBest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");
  const [genBusyId, setGenBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([
      api.get("/admin/research/opportunities"),
      api.get("/admin/research/next-best"),
    ]).then(([o, n]) => {
      setOpps(o.data || []);
      setNextBest(n.data && n.data.id ? n.data : null);
      return o.data || [];
    }).catch(() => null).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const pollUntil = useCallback(async (checkFn, { interval = 6000, maxTries = 15 } = {}) => {
    for (let i = 0; i < maxTries; i++) {
      await new Promise((r) => setTimeout(r, interval));
      const fresh = await load();
      if (fresh && checkFn(fresh)) return;
    }
  }, [load]);

  const runResearch = async () => {
    setRunning(true); setMsg("");
    const beforeCount = opps.length;
    try {
      const r = await api.post("/admin/research/run", { n: 3 });
      setMsg(r?.data?.message || "Researching… (usually 30-50s)");
      pollUntil((fresh) => fresh.length > beforeCount, { interval: 6000, maxTries: 15 });
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Research failed");
    } finally {
      setRunning(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/research/opportunities/${id}`, { status });
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Update failed");
    }
  };

  const generateFromOpp = async (id) => {
    setGenBusyId(id); setMsg("");
    try {
      const r = await api.post(`/admin/research/opportunities/${id}/generate`);
      setMsg(r?.data?.message || "Generating… (usually 30-150s)");
      await pollUntil(
        (fresh) => { const o = fresh.find((x) => x.id === id); return o && o.status !== "IN DEVELOPMENT"; },
        { interval: 8000, maxTries: 20 },
      );
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenBusyId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="research-tab">
      <div className="dash-card p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-display text-xl font-bold tracking-tight">AI Research Analyst</h3>
            <p className="text-sm text-white/60 mt-1 max-w-2xl">
              Analyzes real Meridian catalog/revenue data + market reasoning to propose scored product
              opportunities. Every claim is labeled VERIFIED / SIGNAL / INFERENCE / ASSUMPTION — nothing is
              invented. Runs automatically every {RESEARCH_HOURS}h; you can also trigger it manually (usually
              takes 30-50s). Nothing is ever auto-built or auto-published.
            </p>
          </div>
          <button data-testid="run-research-btn" onClick={runResearch} disabled={running} className="dash-btn inline-flex items-center gap-2">
            {running ? "Researching…" : <><Sparkles className="w-4 h-4" /> Run Research</>}
          </button>
        </div>
        {msg && <div data-testid="research-msg" className="mt-3 text-xs text-white/60 font-mono-ui">{msg}</div>}
      </div>

      {nextBest && (
        <div className="dash-card p-6" data-testid="next-best-product">
          <div className="label-mono text-[#4CE0B3] mb-2">/ NEXT BEST PRODUCT</div>
          <h3 className="font-display text-2xl font-bold tracking-tight">{nextBest.topic}</h3>
          <p className="text-sm text-white/60 mt-1">{nextBest.why_selected}</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
            <div><span className="label-mono text-white/40">TARGET CUSTOMER</span><div>{nextBest.target_customer}</div></div>
            <div><span className="label-mono text-white/40">PROBLEM</span><div>{nextBest.problem}</div></div>
            <div><span className="label-mono text-white/40">DESIRED OUTCOME</span><div>{nextBest.desired_outcome}</div></div>
            <div><span className="label-mono text-white/40">MARKETING ANGLE</span><div>{nextBest.marketing_angle}</div></div>
            <div><span className="label-mono text-white/40">RECOMMENDED</span><div>{nextBest.recommended_category} · {nextBest.recommended_price_tier}</div></div>
            <div><span className="label-mono text-white/40">EVIDENCE / CONFIDENCE</span><div>{nextBest.evidence_level} · {nextBest.confidence}</div></div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <span className="label-mono" style={{ color: OPP_STATUS_COLOR[nextBest.recommended_action] || "#8a8b8f" }}>
              SCORE {nextBest.avg_score}/10 · {nextBest.recommended_action}
            </span>
            <button
              data-testid={`next-best-generate-btn`}
              onClick={() => generateFromOpp(nextBest.id)}
              disabled={genBusyId === nextBest.id}
              className="dash-btn ml-auto"
            >
              {genBusyId === nextBest.id ? "Generating…" : "Generate this product →"}
            </button>
          </div>
        </div>
      )}

      <div className="dash-card overflow-hidden">
        <div className="p-4 border-b border-[#222] flex items-center justify-between">
          <h3 className="font-display text-lg font-bold tracking-tight">Opportunities</h3>
          <span className="label-mono text-white/40">{opps.length} logged</span>
        </div>
        {loading && <div className="p-4 text-sm text-white/40 font-mono-ui">Loading…</div>}
        {!loading && opps.length === 0 && (
          <div className="p-6 text-sm text-white/40 font-mono-ui">No research yet — click "Run Research" above.</div>
        )}
        <div className="divide-y divide-[#1A1A1A]">
          {opps.map((o) => (
            <div key={o.id} data-testid={`opportunity-${o.id}`} className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-display font-semibold">{o.topic}</div>
                  <div className="text-xs text-white/50 mt-0.5">{o.target_customer}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="label-mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#1A1A1A", color: "#8a8b8f" }}>{o.market_status}</span>
                  <span className="label-mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#1A1A1A", color: "#8a8b8f" }}>{o.evidence_level}</span>
                  <span className="label-mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#1A1A1A", color: "#4CE0B3" }}>SCORE {o.avg_score}/10</span>
                  <span data-testid={`opp-status-${o.id}`} className="label-mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#1A1A1A", color: OPP_STATUS_COLOR[o.status] || "#8a8b8f" }}>{o.status}</span>
                </div>
              </div>
              <p className="text-sm text-white/70 mt-2">{o.problem}</p>
              <div className="mt-2 text-xs text-white/50">Why: {o.why_selected}</div>
              {o.quality_review && (
                <div className="mt-1 text-xs" style={{ color: o.quality_review.verdict === "PASS" ? "#4CE0B3" : "#eab308" }}>
                  Quality review: {o.quality_review.verdict}{o.quality_review.reasons?.length ? ` — ${o.quality_review.reasons[0]}` : ""}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {["WATCH", "BUILD", "REJECTED"].map((s) => (
                  <button
                    key={s}
                    data-testid={`opp-set-${s.toLowerCase()}-${o.id}`}
                    onClick={() => setStatus(o.id, s)}
                    disabled={o.status === "IN DEVELOPMENT"}
                    className="dash-btn-ghost text-xs"
                    style={o.status === s ? { background: OPP_STATUS_COLOR[s], color: "#0A0A0A" } : {}}
                  >
                    {s}
                  </button>
                ))}
                {o.status === "BUILD" && !o.generated_product_id && (
                  <button
                    data-testid={`generate-opp-${o.id}`}
                    onClick={() => generateFromOpp(o.id)}
                    disabled={genBusyId === o.id}
                    className="dash-btn text-xs ml-auto"
                  >
                    {genBusyId === o.id ? "Generating…" : "Generate product →"}
                  </button>
                )}
                {o.generated_product_id && (
                  <span className="ml-auto label-mono text-[10px] text-white/40">→ {o.generated_product_title || "product"} ({o.status})</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
const RESEARCH_HOURS = 72;

// ─── Social Queue Tab: pre-drafted IG + X posts for one-click copy ───
function SocialQueueTab() {
  const [items, setItems] = useState([]);
  const [copied, setCopied] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/social-queue")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async (id, text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ id, which });
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error("clipboard copy failed", err);
    }
  };

  const markPosted = async (id) => {
    try {
      await api.post(`/admin/social-queue/${id}/mark-posted`);
      load();
    } catch (err) {
      console.error("mark-posted failed", err);
    }
  };

  const downloadImage = (imgData, imgUrl, title) => {
    const url = imgData ? `data:image/png;base64,${imgData}` : imgUrl;
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
    a.click();
  };

  if (loading) return <div className="text-white/40 font-mono-ui">Loading queue…</div>;

  return (
    <div className="space-y-4" data-testid="social-queue-tab">
      <div className="dash-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-display text-xl font-bold tracking-tight">One-click social posting</h3>
            <p className="text-sm text-white/60 mt-1">
              Every AI-generated product produces a ready-to-post Instagram + X caption + image below.
              Buffer's public API is currently restricted, so we skip auto-posting to avoid failures —
              tap <em>Copy</em>, paste into your app, done in 30 seconds.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <LaunchKitButton onDone={load} />
            <button onClick={load} className="dash-btn-ghost">Refresh</button>
          </div>
        </div>
      </div>

      {items.length === 0 && (
        <div className="dash-card p-6 text-sm text-white/60">
          Nothing in the queue. Run an auto-drop or generate a product to fill it.
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} className="dash-card p-5" data-testid={`social-post-${it.id}`}>
            <div className="flex gap-4">
              <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-[#141414]">
                {(it.image_data || it.image_url) && (
                  <img
                    src={it.image_data ? `data:image/png;base64,${it.image_data}` : it.image_url}
                    alt="" className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-lg truncate">{it.product_title}</div>
                {it.campaign === "launch_kit_v1" && (
                  <div className="inline-flex items-center gap-2 mt-1 text-xs font-mono-ui px-2 py-0.5 rounded-full"
                       style={{ background: "#B14D2A", color: "#F7F4ED" }}>
                    LAUNCH KIT · DAY {it.day} · {(it.theme || "").toUpperCase()}
                  </div>
                )}
                <a href={it.link} target="_blank" rel="noreferrer"
                   className="label-mono text-white/40 underline underline-offset-2 hover:text-white/70 truncate block">
                  {it.link}
                </a>
                <button
                  onClick={() => downloadImage(it.image_data, it.image_url, it.product_title)}
                  className="mt-2 dash-btn-ghost text-xs inline-flex items-center gap-1"
                >
                  Download image
                </button>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {[
                ["instagram", "Instagram"],
                ["twitter", "X (Twitter)"],
              ].map(([key, label]) => (
                <div key={key} className="rounded-md p-3 bg-[#0e0e0e]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="label-mono text-white/50">{label.toUpperCase()}</span>
                    <button
                      data-testid={`copy-${key}-${it.id}`}
                      onClick={() => copy(it.id, it[key] || "", key)}
                      className="text-xs px-3 py-1 rounded-full bg-[#FF3B30] text-white"
                    >
                      {copied && copied.id === it.id && copied.which === key ? "Copied ✓" : "Copy"}
                    </button>
                  </div>
                  <p className="text-sm text-white/80 whitespace-pre-wrap">{it[key]}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                data-testid={`mark-posted-${it.id}`}
                onClick={() => markPosted(it.id)}
                className="dash-btn-ghost text-xs"
              >
                Mark as posted →
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── Integrations Panel: live status of every 3rd-party service ───
function IntegrationsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/integrations").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);
  if (!data) return <div className="dash-card p-5 text-white/40 font-mono-ui">Loading integrations…</div>;

  const stripeMode = data.stripe?.mode;
  const stripeNote = data.stripe?.configured
    ? (stripeMode === "live"
        ? "Live mode — real charges enabled."
        : stripeMode === "test"
          ? "Test mode — swap to sk_live_… before launch."
          : "Configured — key prefix not recognized.")
    : "Missing STRIPE_API_KEY.";

  const rows = [
    { key: "stripe", label: "Stripe · Payments",
      note: stripeNote },
    { key: "llm", label: "Claude + Nano Banana · AI",
      note: data.llm?.configured ? "Emergent Universal Key. Powers product copy, images & assets." : "Missing EMERGENT_LLM_KEY." },
    { key: "resend", label: "Resend · Emails",
      note: data.resend?.configured ? `Sender: ${data.resend.sender}. Recovery + announcement emails live.` : "Missing RESEND_API_KEY." },
    { key: "printful", label: "Printful · Physical fulfillment",
      note: data.printful?.configured
        ? (data.printful.store
            ? `Connected to "${data.printful.store.name}". Physical orders will log a fulfillment reminder in your activity feed.`
            : "API key is set but no store returned.")
        : "Missing PRINTFUL_API_KEY." },
    { key: "buffer", label: "Buffer · Auto-post",
      note: data.buffer?.configured
        ? (data.buffer.note || `${data.buffer.profiles.length} channels connected — auto-posting enabled.`)
        : "Not configured — social posts live in the Social Queue for one-click copy." },
  ];

  return (
    <div className="dash-card p-5" data-testid="integrations-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-bold tracking-tight">Integrations</h3>
        <span className="label-mono text-white/40">STATUS</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {rows.map((r) => {
          const on = data[r.key]?.configured;
          return (
            <div key={r.key} className="rounded-md p-3 bg-[#0e0e0e] flex gap-3" data-testid={`integration-${r.key}`}>
              <div className="mt-1 w-2 h-2 rounded-full flex-shrink-0"
                   style={{ background: on ? "#22c55e" : "#6b7280" }} />
              <div className="min-w-0">
                <div className="font-display text-sm">{r.label}</div>
                <div className="label-mono text-white/40 mt-0.5">{on ? "CONNECTED" : "NOT CONFIGURED"}</div>
                <div className="text-xs text-white/60 mt-1">{r.note}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Printful Sync Card: auto-publish AI artwork to physical SKUs ───
function PrintfulSyncCard() {
  const [data, setData] = useState({ synced: [], syncable_remaining: 0 });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    api.get("/admin/printful/synced").then((r) => setData(r.data || { synced: [], syncable_remaining: 0 })).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runBatch = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/admin/printful/sync-batch");
      setMsg(`Scheduled ${r.data.scheduled} artworks — publishing to Printful + storefront in the background. Refresh in ~1 min.`);
      setTimeout(load, 5000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to start batch sync.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dash-card p-5" data-testid="printful-sync-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl font-bold tracking-tight">Printful · Auto-publish physical merch</h3>
          <p className="text-sm text-white/60 mt-1">
            One click — every AI-generated artwork becomes a tee, hoodie, poster, mug, and tote,
            published to your Printful store <em>and</em> auto-listed on the Meridian storefront.
            Paid orders auto-log a fulfillment reminder.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="dash-btn-ghost">Refresh</button>
          <button
            data-testid="printful-batch-sync-btn"
            onClick={runBatch}
            disabled={busy || data.syncable_remaining === 0}
            className="dash-btn"
          >
            {busy ? "Starting…" : data.syncable_remaining > 0
              ? `Sync ${data.syncable_remaining} artworks →`
              : "All artworks synced"}
          </button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3 text-center">
        <div className="dash-card p-3">
          <div className="label-mono text-white/50">SYNCED</div>
          <div data-testid="printful-synced-count" className="font-display text-2xl mt-1">{data.synced.length}</div>
        </div>
        <div className="dash-card p-3">
          <div className="label-mono text-white/50">REMAINING</div>
          <div className="font-display text-2xl mt-1">{data.syncable_remaining}</div>
        </div>
        <div className="dash-card p-3">
          <div className="label-mono text-white/50">SKUs LIVE</div>
          <div className="font-display text-2xl mt-1">{data.synced.reduce((n, s) => n + (s.printful_variants?.length || 0), 0)}</div>
        </div>
      </div>
      {msg && <div data-testid="printful-msg" className="mt-3 text-xs text-white/60">{msg}</div>}
      {data.synced.length > 0 && (
        <div className="mt-4">
          <div className="label-mono text-white/40 mb-2">RECENTLY SYNCED</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.synced.slice(0, 6).map((s) => (
              <div key={s.id} className="rounded-md p-3 bg-[#0e0e0e] flex gap-3">
                <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 bg-[#141414]">
                  {(s.image_data || s.image_url) && (
                    <img
                      src={s.image_data ? `data:image/png;base64,${s.image_data}` : s.image_url}
                      alt="" className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-display truncate">{s.title}</div>
                  <div className="label-mono text-white/40 mt-0.5">
                    {(s.printful_variants || []).length} SKUs · Printful id {s.printful_sync_product_id}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function LaunchKitButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const run = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/admin/launch-kit/generate");
      if (r.data?.already_generated) {
        setMsg("Already generated — scroll down to see the 3-day kit.");
      } else {
        setMsg(`3-day launch kit ready — ${r.data.generated} posts added to queue.`);
        onDone();
      }
    } catch (err) {
      console.error("launch kit failed", err);
      setMsg(err?.response?.data?.detail || "Generation failed. Try again in a moment.");
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(""), 6000);
    }
  };
  return (
    <div className="flex items-center gap-3">
      <button
        data-testid="launch-kit-btn"
        onClick={run}
        disabled={busy}
        className="dash-btn"
      >
        {busy ? "Writing…" : "Generate 3-day launch kit ✨"}
      </button>
      {msg && <div data-testid="launch-kit-msg" className="text-xs text-white/60">{msg}</div>}
    </div>
  );
}

